/**
 * Contact Service - Specialized service for contact form operations
 */

import apiService, { ContactFormData, ContactFormResponse, ApiError } from './ApiService';

export interface ContactFormSubmissionResult {
  success: boolean;
  message: string;
  requestId?: string;
  errors?: Array<{ field: string; message: string }>;
}

class ContactService {
  /**
   * Submit contact form with enhanced error handling
   */
  async submitForm(formData: ContactFormData): Promise<ContactFormSubmissionResult> {
    try {
      const response = await apiService.submitContactForm(formData);
      
      return {
        success: true,
        message: response.message,
        requestId: response.request_id,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        return {
          success: false,
          message: error.message,
          errors: error.getValidationErrors(),
        };
      }
      
      return {
        success: false,
        message: 'An unexpected error occurred. Please try again.',
        errors: [],
      };
    }
  }

  /**
   * Validate form data before submission
   */
  validateFormData(data: ContactFormData): Array<{ field: string; message: string }> {
    const errors: Array<{ field: string; message: string }> = [];

    // Required field validation
    if (!data.name?.trim()) {
      errors.push({ field: 'name', message: 'Name is required' });
    }

    if (!data.email?.trim()) {
      errors.push({ field: 'email', message: 'Email is required' });
    } else if (!this.isValidEmail(data.email)) {
      errors.push({ field: 'email', message: 'Please enter a valid email address' });
    }

    if (!data.company?.trim()) {
      errors.push({ field: 'company', message: 'Company name is required' });
    }

    if (!data.message?.trim()) {
      errors.push({ field: 'message', message: 'Message is required' });
    } else if (data.message.length < 10) {
      errors.push({ field: 'message', message: 'Message must be at least 10 characters long' });
    } else if (data.message.length > 2000) {
      errors.push({ field: 'message', message: 'Message cannot exceed 2000 characters' });
    }

    // Phone validation (if provided)
    if (data.phone && !this.isValidPhone(data.phone)) {
      errors.push({ field: 'phone', message: 'Please enter a valid phone number' });
    }

    return errors;
  }

  /**
   * Email validation
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Phone validation - supports various formats
   */
  private isValidPhone(phone: string): boolean {
    // Remove all non-digit characters for length check
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Must have at least 10 digits
    if (digitsOnly.length < 10) {
      return false;
    }
    
    // Allow various phone formats
    const phoneRegex = /^[\+]?[()\s\-\.0-9]{10,}$/;
    return phoneRegex.test(phone);
  }

  /**
   * Get project types from API service
   */
  getProjectTypes(): string[] {
    return apiService.getProjectTypes();
  }

  /**
   * Get budget ranges from API service
   */
  getBudgetRanges(): string[] {
    return apiService.getBudgetRanges();
  }

  /**
   * Format success message with request ID
   */
  formatSuccessMessage(message: string, requestId?: string): string {
    if (requestId) {
      return `${message} Your request ID is: ${requestId}`;
    }
    return message;
  }

  /**
   * Check if API is available
   */
  async checkApiHealth(): Promise<boolean> {
    try {
      await apiService.healthCheck();
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
const contactService = new ContactService();
export default contactService;